import { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { motion, useMotionTemplate, useMotionValue, useScroll, useTransform } from "framer-motion";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { ArrowRight, CalendarDays, PhoneCall, Sparkles, Star, WandSparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import {
  BUSINESS,
  FAQS,
  HERO_FRAMES,
  LOCATION_PAGES,
  MANIFESTO_CHAPTERS,
  MARQUEE_WORDS,
  SERVICE_PAGES,
  TIME_SLOTS,
} from "@/data/siteContent";

const sectionReveal = {
  hidden: { opacity: 0, y: 56 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  },
};

const GALLERY_FILTER_ORDER = ["all", "interior", "cabinet", "trim-doors", "exterior"];
const GALLERY_DISPLAY_LIMIT = 6;
const GALLERY_CATEGORY_PRIORITY = {
  interior: 0,
  cabinet: 1,
  "trim-doors": 2,
  exterior: 3,
};
const DEFAULT_BOOKING_NOTICE_DAYS = 4;

const formatFilterLabel = (filter) => (filter === "trim-doors" ? "trim & doors" : filter.replace(/-/g, " "));

const getRotationStyle = (rotation = 0, scale = 1.1) =>
  rotation
    ? {
        transform: `rotate(${rotation}deg) scale(${scale})`,
        transformOrigin: "center center",
      }
    : undefined;

const QuoteDialog = ({ open, onOpenChange, estimateSnapshot, onSuccess }) => {
  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    service_type: estimateSnapshot?.service_type || "Interior Painting",
    room_type: estimateSnapshot?.room_type || "Living room",
    square_feet: estimateSnapshot?.square_feet || 450,
    timeline: estimateSnapshot?.timeline || "within-3-weeks",
    city: estimateSnapshot?.city || "Toronto",
    name: "",
    email: "",
    phone: "",
    notes: estimateSnapshot?.notes || "",
  });

  useEffect(() => {
    if (!open) {
      setStep(1);
    }
  }, [open]);

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const lead = await api.createLead({
        ...form,
        source: estimateSnapshot ? "estimator" : "quote_form",
        estimate_snapshot: estimateSnapshot?.result || null,
      });
      toast.success("Quote request sent", {
        description: `We saved ${lead.name}'s request and queued a fast follow-up.`,
      });
      onSuccess?.();
      onOpenChange(false);
      setForm((current) => ({ ...current, name: "", email: "", phone: "", notes: "" }));
    } catch (error) {
      toast.error("We couldn't send that yet", {
        description: error?.response?.data?.detail || "Please try again in a moment.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="quote-dialog-shell border-white/10 bg-[#08131f] text-[#f4efe5] sm:max-w-[720px]" data-testid="quote-dialog-content">
        <DialogHeader>
          <DialogTitle className="font-display text-4xl tracking-[-0.08em]" data-testid="quote-dialog-title">
            Get your tailored quote.
          </DialogTitle>
          <DialogDescription className="text-[#f4efe5]/70" data-testid="quote-dialog-description">
            A short form, a fast callback, and an estimate that already starts in the right range.
          </DialogDescription>
        </DialogHeader>

        <div className="quote-stepper" data-testid="quote-dialog-stepper">
          {[1, 2, 3].map((value) => (
            <div key={value} className={`quote-step ${step === value ? "is-active" : ""}`} data-testid={`quote-step-${value}`}>
              0{value}
            </div>
          ))}
        </div>

        {step === 1 ? (
          <div className="quote-grid" data-testid="quote-dialog-step-one">
            <div>
              <label className="quote-label">Service</label>
              <Select
                value={form.service_type}
                onValueChange={(value) => setForm((current) => ({ ...current, service_type: value }))}
              >
                <SelectTrigger data-testid="quote-service-select-trigger">
                  <SelectValue placeholder="Choose a service" />
                </SelectTrigger>
                <SelectContent>
                  {["Interior Painting", "Exterior Painting", "Cabinet Painting", "Trim & Doors"].map((service) => (
                    <SelectItem key={service} value={service} data-testid={`quote-service-option-${service.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                      {service}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="quote-label">Room / area</label>
              <Input
                value={form.room_type}
                onChange={(event) => setForm((current) => ({ ...current, room_type: event.target.value }))}
                data-testid="quote-room-type-input"
              />
            </div>
            <div>
              <label className="quote-label">Approx. square feet</label>
              <Input
                type="number"
                value={form.square_feet}
                onChange={(event) => setForm((current) => ({ ...current, square_feet: Number(event.target.value) }))}
                data-testid="quote-square-feet-input"
              />
            </div>
            <div>
              <label className="quote-label">Timeline</label>
              <Select
                value={form.timeline}
                onValueChange={(value) => setForm((current) => ({ ...current, timeline: value }))}
              >
                <SelectTrigger data-testid="quote-timeline-select-trigger">
                  <SelectValue placeholder="Choose a timeline" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="flexible" data-testid="quote-timeline-option-flexible">Flexible</SelectItem>
                  <SelectItem value="within-3-weeks" data-testid="quote-timeline-option-three-weeks">Within 3 weeks</SelectItem>
                  <SelectItem value="asap" data-testid="quote-timeline-option-asap">ASAP</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : null}

        {step === 2 ? (
          <div className="quote-grid" data-testid="quote-dialog-step-two">
            <div>
              <label className="quote-label">Name</label>
              <Input
                value={form.name}
                onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))}
                data-testid="quote-name-input"
              />
            </div>
            <div>
              <label className="quote-label">Email</label>
              <Input
                type="email"
                value={form.email}
                onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))}
                data-testid="quote-email-input"
              />
            </div>
            <div>
              <label className="quote-label">Phone</label>
              <Input
                value={form.phone}
                onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))}
                data-testid="quote-phone-input"
              />
            </div>
            <div>
              <label className="quote-label">City</label>
              <Select
                value={form.city}
                onValueChange={(value) => setForm((current) => ({ ...current, city: value }))}
              >
                <SelectTrigger data-testid="quote-city-select-trigger">
                  <SelectValue placeholder="Choose a city" />
                </SelectTrigger>
                <SelectContent>
                  {LOCATION_PAGES.map((location) => (
                    <SelectItem key={location.city} value={location.city} data-testid={`quote-city-option-${location.slug}`}>
                      {location.city}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ) : null}

        {step === 3 ? (
          <div className="quote-grid quote-grid-wide" data-testid="quote-dialog-step-three">
            <div className="quote-summary-card" data-testid="quote-summary-card">
              <p className="quote-label">Your request snapshot</p>
              <h3 data-testid="quote-summary-heading">
                {form.service_type} · {form.room_type}
              </h3>
              <p data-testid="quote-summary-copy">
                {form.square_feet} sq ft in {form.city}. Timeline: {form.timeline.replace(/-/g, " ")}.
              </p>
              {estimateSnapshot?.result ? (
                <div className="estimate-chip" data-testid="quote-summary-estimate">
                  Preliminary range ${estimateSnapshot.result.range_low}–${estimateSnapshot.result.range_high}
                </div>
              ) : null}
            </div>
            <div>
              <label className="quote-label">Anything we should know before we call?</label>
              <Textarea
                value={form.notes}
                onChange={(event) => setForm((current) => ({ ...current, notes: event.target.value }))}
                className="min-h-[150px]"
                data-testid="quote-notes-input"
              />
            </div>
          </div>
        ) : null}

        <div className="quote-actions" data-testid="quote-dialog-actions">
          <Button
            variant="outline"
            className="rounded-full border-white/15 bg-transparent text-[#f4efe5] hover:bg-white/10"
            onClick={() => setStep((current) => Math.max(1, current - 1))}
            disabled={step === 1 || submitting}
            data-testid="quote-back-button"
          >
            Back
          </Button>
          {step < 3 ? (
            <Button className="rounded-full bg-[#c76b4f] text-white hover:bg-[#d1785b]" onClick={() => setStep((current) => current + 1)} data-testid="quote-next-button">
              Next step
            </Button>
          ) : (
            <Button className="rounded-full bg-[#c76b4f] text-white hover:bg-[#d1785b]" onClick={handleSubmit} disabled={submitting} data-testid="quote-submit-button">
              {submitting ? "Sending…" : "Request my quote"}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const OfferDialog = ({ open, onOpenChange }) => {
  const [claiming, setClaiming] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });

  const submitOffer = async () => {
    setClaiming(true);
    try {
      await api.createLead({
        ...form,
        city: "Toronto",
        service_type: "Interior Painting",
        room_type: "Offer inquiry",
        square_feet: 0,
        timeline: "flexible",
        notes: "$100 offer popup claimed",
        source: "popup_offer",
        estimate_snapshot: null,
      });
      localStorage.setItem("northline-offer-claimed", "true");
      toast.success("Offer claimed", {
        description: "We saved your details and will follow up with next steps.",
      });
      onOpenChange(false);
      setForm({ name: "", email: "", phone: "" });
    } catch (error) {
      toast.error("Couldn't claim the offer yet", {
        description: error?.response?.data?.detail || "Please try again in a moment.",
      });
    } finally {
      setClaiming(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="quote-dialog-shell border-white/10 bg-[#08131f] text-[#f4efe5] sm:max-w-[540px]" data-testid="offer-dialog-content">
        <DialogHeader>
          <DialogTitle className="font-display text-4xl tracking-[-0.08em]" data-testid="offer-dialog-title">
            Claim {BUSINESS.offer}
          </DialogTitle>
          <DialogDescription className="text-[#f4efe5]/70" data-testid="offer-dialog-description">
            Leave your details and we’ll lock in the promotion before your on-site estimate.
          </DialogDescription>
        </DialogHeader>
        <div className="quote-grid quote-grid-wide" data-testid="offer-dialog-form">
          <Input placeholder="Name" value={form.name} onChange={(event) => setForm((current) => ({ ...current, name: event.target.value }))} data-testid="offer-name-input" />
          <Input placeholder="Email" type="email" value={form.email} onChange={(event) => setForm((current) => ({ ...current, email: event.target.value }))} data-testid="offer-email-input" />
          <Input placeholder="Phone" value={form.phone} onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))} data-testid="offer-phone-input" />
        </div>
        <Button className="rounded-full bg-[#c76b4f] text-white hover:bg-[#d1785b]" onClick={submitOffer} disabled={claiming} data-testid="offer-submit-button">
          {claiming ? "Claiming…" : "Claim my offer"}
        </Button>
      </DialogContent>
    </Dialog>
  );
};

export const HomePage = ({ siteData }) => {
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [offerOpen, setOfferOpen] = useState(false);
  const [galleryFilter, setGalleryFilter] = useState("all");
  const [estimateSubmitting, setEstimateSubmitting] = useState(false);
  const [estimateResult, setEstimateResult] = useState(null);
  const [beforeAfterRatio, setBeforeAfterRatio] = useState(58);
  const [estimateForm, setEstimateForm] = useState({
    service_type: "Interior Painting",
    room_type: "Living room",
    square_feet: 420,
    surfaces: 4,
    finish_level: "premium",
    condition: "average-walls",
    timeline: "within-3-weeks",
    city: "Toronto",
    notes: "",
  });
  const [bookingDate, setBookingDate] = useState(dayjs().add(DEFAULT_BOOKING_NOTICE_DAYS, "day").toDate());
  const [bookingForm, setBookingForm] = useState({
    name: "",
    email: "",
    phone: "",
    city: "Toronto",
    service_type: "Interior Painting",
    notes: "",
    time_label: TIME_SLOTS[0],
  });
  const [bookingSubmitting, setBookingSubmitting] = useState(false);
  const rotateX = useMotionValue(0);
  const rotateY = useMotionValue(0);
  const heroGlow = useMotionTemplate`radial-gradient(circle at 50% 50%, rgba(199, 107, 79, ${useTransform(rotateY, [-8, 8], [0.12, 0.24])}), transparent 62%)`;
  const { scrollY } = useScroll();
  const heroY = useTransform(scrollY, [0, 600], [0, 90]);
  const minimumBookingNoticeDays = siteData?.booking_rules?.minimum_notice_days ?? DEFAULT_BOOKING_NOTICE_DAYS;
  const bookingMinDate = useMemo(
    () => dayjs().add(minimumBookingNoticeDays, "day").startOf("day").toDate(),
    [minimumBookingNoticeDays]
  );
  const { data: bookings, refetch: refetchBookings } = useQuery({
    queryKey: ["site-bookings"],
    queryFn: api.getBookings,
  });
  const availableGalleryFilters = useMemo(() => {
    const categories = Array.from(new Set((siteData?.gallery || []).map((item) => item.category).filter(Boolean)));
    return GALLERY_FILTER_ORDER.filter((filter) => filter === "all" || categories.includes(filter));
  }, [siteData?.gallery]);

  const filteredGallery = useMemo(() => {
    const source = siteData?.gallery || [];
    return galleryFilter === "all"
      ? source
      : source.filter((item) => item.category === galleryFilter);
  }, [galleryFilter, siteData?.gallery]);

  const curatedGallery = useMemo(() => {
    const sorted = [...filteredGallery].sort((left, right) => {
      const categoryGap = (GALLERY_CATEGORY_PRIORITY[left.category] ?? 99) - (GALLERY_CATEGORY_PRIORITY[right.category] ?? 99);
      if (categoryGap !== 0) return categoryGap;
      if (left.featured !== right.featured) return left.featured ? -1 : 1;
      return left.title.localeCompare(right.title);
    });

    if (galleryFilter === "all") {
      return sorted.slice(0, GALLERY_DISPLAY_LIMIT);
    }
    return sorted;
  }, [filteredGallery, galleryFilter]);

  const comparisonItem = (siteData?.gallery || []).find((item) => item.before_image_url && item.after_image_url) || siteData?.gallery?.[0];
  const bookedSlots = useMemo(() => {
    const selectedDay = dayjs(bookingDate).format("YYYY-MM-DD");
    return new Set(
      (bookings || [])
        .filter((item) => item.date === selectedDay)
        .map((item) => item.time_label)
    );
  }, [bookingDate, bookings]);

  useEffect(() => {
    document.title = `${BUSINESS.name} | Toronto Residential Painters`;
    let hasInteracted = false;
    const markInteracted = () => {
      hasInteracted = true;
    };
    const timer = window.setTimeout(() => {
      const claimed = localStorage.getItem("northline-offer-claimed");
      const dismissed = localStorage.getItem("northline-offer-dismissed");
      if (!claimed && !dismissed && !hasInteracted && window.scrollY < 160) {
        setOfferOpen(true);
      }
    }, 3200);

    window.addEventListener("pointerdown", markInteracted, { passive: true });
    window.addEventListener("keydown", markInteracted);
    window.addEventListener("scroll", markInteracted, { passive: true });

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("pointerdown", markInteracted);
      window.removeEventListener("keydown", markInteracted);
      window.removeEventListener("scroll", markInteracted);
    };
  }, []);

  useEffect(() => {
    if (dayjs(bookingDate).isBefore(bookingMinDate, "day")) {
      setBookingDate(bookingMinDate);
    }
  }, [bookingDate, bookingMinDate]);

  const handleHeroMove = (event) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    const px = ((event.clientX - bounds.left) / bounds.width - 0.5) * 2;
    const py = ((event.clientY - bounds.top) / bounds.height - 0.5) * 2;
    rotateY.set(px * 7);
    rotateX.set(py * -7);
  };

  const submitEstimate = async () => {
    setEstimateSubmitting(true);
    try {
      const result = await api.createEstimate(estimateForm);
      setEstimateResult({
        ...estimateForm,
        result,
      });
      toast.success("Instant estimate ready", {
        description: `Preliminary range: $${result.range_low}–$${result.range_high}.`,
      });
    } catch (error) {
      toast.error("Estimator unavailable", {
        description: error?.response?.data?.detail || "Please try again shortly.",
      });
    } finally {
      setEstimateSubmitting(false);
    }
  };

  const submitBooking = async () => {
    setBookingSubmitting(true);
    try {
      await api.createBooking({
        ...bookingForm,
        date: dayjs(bookingDate).format("YYYY-MM-DD"),
      });
      toast.success("Estimate visit booked", {
        description: `${dayjs(bookingDate).format("MMMM D")} at ${bookingForm.time_label} is saved.`,
      });
      setBookingForm((current) => ({ ...current, name: "", email: "", phone: "", notes: "" }));
      await refetchBookings();
    } catch (error) {
      toast.error("That slot just closed", {
        description: error?.response?.data?.detail || "Please choose another time.",
      });
    } finally {
      setBookingSubmitting(false);
    }
  };

  return (
    <div className="page-shell home-page" data-testid="home-page-root">
      <OfferDialog
        open={offerOpen}
        onOpenChange={(next) => {
          if (!next) {
            localStorage.setItem("northline-offer-dismissed", "true");
          }
          setOfferOpen(next);
        }}
      />
      <QuoteDialog open={quoteOpen} onOpenChange={setQuoteOpen} estimateSnapshot={estimateResult} />

      <header className="site-header" data-testid="site-header">
        <div className="site-brand" data-testid="site-brand-mark">GTA Modern</div>
        <nav className="site-nav" data-testid="site-navigation">
          <a href="#services" data-testid="nav-services-link">Services</a>
          <a href="#estimate" data-testid="nav-estimate-link">Estimator</a>
          <a href="#projects" data-testid="nav-projects-link">Projects</a>
          <a href="#booking" data-testid="nav-booking-link">Booking</a>
          <Link to="/admin" data-testid="nav-admin-link">Admin</Link>
        </nav>
        <div className="site-header-actions">
          <a href={BUSINESS.phoneHref} className="site-phone-link" data-testid="header-phone-link">
            {BUSINESS.phoneDisplay}
          </a>
          <Button className="rounded-full bg-[#c76b4f] px-5 text-white hover:bg-[#d1785b]" onClick={() => setQuoteOpen(true)} data-testid="header-quote-button">
            Get free quote
          </Button>
        </div>
      </header>

      <section className="hero-section" data-testid="hero-section">
        <motion.div className="hero-copy" initial="hidden" animate="visible" variants={sectionReveal}>
          <div className="eyebrow-row" data-testid="hero-eyebrow-row">
            <span className="eyebrow-pill">Toronto GTA residential painting</span>
            <span className="eyebrow-pill muted">14 years · 480+ completed projects</span>
          </div>
          <div className="hero-title-wrap" data-testid="hero-title-wrap">
            {["Toronto’s trusted", "residential painters", "with editorial finish."].map((line, index) => (
              <div className="hero-line-mask" key={line} data-testid={`hero-line-mask-${index + 1}`}>
                <motion.h1
                  className="hero-line"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.15 + index * 0.12, duration: 0.95, ease: [0.22, 1, 0.36, 1] }}
                  data-testid={`hero-line-${index + 1}`}
                >
                  {line}
                </motion.h1>
              </div>
            ))}
          </div>
          <p className="hero-lead" data-testid="hero-lead-copy">
            A premium quote-first website experience for homeowners who want confidence before the first brush stroke: instant ranges, proof of work, and zero-friction booking.
          </p>
          <div className="hero-cta-row" data-testid="hero-cta-row">
            <Button className="cta-primary" onClick={() => setQuoteOpen(true)} data-testid="hero-primary-cta-button">
              Get my free quote <ArrowRight size={18} />
            </Button>
            <Button variant="outline" className="cta-secondary" onClick={submitEstimate} data-testid="hero-estimator-cta-button">
              Run instant estimate <Sparkles size={18} />
            </Button>
          </div>
          <div className="hero-meta-grid" data-testid="hero-meta-grid">
            {(siteData?.stats || []).map((stat) => (
              <div className="meta-stat" key={stat.label} data-testid={`hero-stat-${stat.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <span>{stat.value}</span>
                <small>{stat.label}</small>
              </div>
            ))}
          </div>
        </motion.div>

        <motion.div className="hero-visual-shell" style={{ y: heroY }} data-testid="hero-visual-shell">
          <motion.div
            className="hero-visual-glow"
            style={{ background: heroGlow }}
            data-testid="hero-visual-glow"
          />
          <motion.div
            className="hero-visual-grid"
            onMouseMove={handleHeroMove}
            onMouseLeave={() => {
              rotateX.set(0);
              rotateY.set(0);
            }}
            style={{ rotateX, rotateY, transformPerspective: 1200 }}
            data-testid="hero-visual-grid"
          >
            {HERO_FRAMES.map((frame, index) => (
              <motion.div className={`hero-frame frame-${index + 1}`} key={frame.title} whileHover={{ y: -10 }} data-testid={`hero-frame-${index + 1}`}>
                {frame.mediaType === "video" ? (
                  <video
                    src={frame.src}
                    poster={frame.poster}
                    className="hero-frame-video"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="metadata"
                    style={getRotationStyle(frame.rotation, 1.08)}
                    data-testid={`hero-frame-video-${index + 1}`}
                  />
                ) : (
                  <img
                    src={frame.src}
                    alt={frame.title}
                    className="hero-frame-image"
                    style={getRotationStyle(frame.rotation, 1.08)}
                    data-testid={`hero-frame-image-${index + 1}`}
                  />
                )}
                <div className="hero-frame-caption" data-testid={`hero-frame-caption-${index + 1}`}>{frame.title}</div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </section>

      <section className="marquee-strip" data-testid="editorial-marquee-strip">
        <div className="marquee-track">
          {[...MARQUEE_WORDS, ...MARQUEE_WORDS].map((word, index) => (
            <span key={`${word}-${index}`} className="marquee-word" data-testid={`marquee-word-${index}`}>{word}</span>
          ))}
        </div>
      </section>

      <motion.section className="chapter-section" id="manifesto" variants={sectionReveal} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.22 }} data-testid="manifesto-section">
        <div className="section-heading-row">
          <p className="section-kicker">Manifesto</p>
          <h2 data-testid="manifesto-heading">Three chapters behind every converted lead.</h2>
        </div>
        <div className="chapter-grid">
          {MANIFESTO_CHAPTERS.map((chapter) => (
            <Card className="chapter-card" key={chapter.number} data-testid={`manifesto-card-${chapter.number}`}>
              <CardContent className="chapter-card-content">
                <span className="chapter-number">{chapter.number}</span>
                <h3>{chapter.title}</h3>
                <p>{chapter.text}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </motion.section>

      <motion.section className="services-section" id="services" variants={sectionReveal} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.18 }} data-testid="services-section">
        <div className="section-heading-row compact">
          <p className="section-kicker">Services</p>
          <h2 data-testid="services-heading">Dedicated landing paths for each high-intent service.</h2>
        </div>
        <div className="services-grid">
          {SERVICE_PAGES.map((service) => (
            <Link to={`/services/${service.slug}`} className="service-card" key={service.slug} data-testid={`service-card-${service.slug}`}>
              <div className="service-card-image-shell">
                <img
                  src={service.image}
                  alt={service.name}
                  className="service-card-image"
                  style={getRotationStyle(service.rotation, 1.08)}
                  data-testid={`service-card-image-${service.slug}`}
                />
              </div>
              <div className="service-card-copy">
                <p>{service.eyebrow}</p>
                <h3>{service.name}</h3>
                <span>{service.summary}</span>
              </div>
            </Link>
          ))}
        </div>
      </motion.section>

      <motion.section className="comparison-section" id="projects" variants={sectionReveal} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.18 }} data-testid="before-after-section">
        <div className="section-heading-row compact">
          <p className="section-kicker">Before / after</p>
          <h2 data-testid="before-after-heading">A tactile slider that turns proof into momentum.</h2>
        </div>
        {comparisonItem ? (
          <div className="before-after-shell" data-testid="before-after-shell">
            <img
              src={comparisonItem.before_image_url}
              alt={`${comparisonItem.title} before`}
              className="before-after-image"
              style={getRotationStyle(comparisonItem.before_image_rotation, 1.08)}
              data-testid="before-after-image-before"
            />
            <div className="before-after-after-layer" style={{ width: `${beforeAfterRatio}%` }} data-testid="before-after-image-after-layer">
              <img
                src={comparisonItem.after_image_url}
                alt={`${comparisonItem.title} after`}
                className="before-after-image"
                style={getRotationStyle(comparisonItem.after_image_rotation, 1.08)}
                data-testid="before-after-image-after"
              />
            </div>
            <div className="before-after-divider" style={{ left: `${beforeAfterRatio}%` }} data-testid="before-after-divider">
              <span>Slide</span>
            </div>
            <input type="range" min="5" max="95" value={beforeAfterRatio} onChange={(event) => setBeforeAfterRatio(Number(event.target.value))} className="before-after-range" data-testid="before-after-range-input" />
          </div>
        ) : null}
      </motion.section>

      <motion.section className="gallery-section" variants={sectionReveal} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.18 }} data-testid="gallery-section">
        <div className="gallery-header">
          <div>
            <p className="section-kicker">Project gallery</p>
            <h2 data-testid="gallery-heading">Real rooms, framed with intent.</h2>
          </div>
          <div className="gallery-filter-row" data-testid="gallery-filter-row">
            {availableGalleryFilters.map((filter) => (
              <button
                type="button"
                key={filter}
                className={`gallery-filter-chip ${galleryFilter === filter ? "active" : ""}`}
                onClick={() => setGalleryFilter(filter)}
                aria-pressed={galleryFilter === filter}
                data-state={galleryFilter === filter ? "active" : "inactive"}
                data-testid={`gallery-filter-${filter}`}
              >
                {formatFilterLabel(filter)}
              </button>
            ))}
          </div>
        </div>
        <div className="gallery-grid" data-testid="gallery-grid">
          {curatedGallery.map((item) => (
            <article className="gallery-card" key={item.id} data-testid={`gallery-card-${item.id}`}>
              <div className="gallery-card-media">
                <img
                  src={item.image_url}
                  alt={item.title}
                  className="gallery-card-image"
                  style={getRotationStyle(item.image_rotation, 1.08)}
                  data-testid={`gallery-card-image-${item.id}`}
                />
              </div>
              <div className="gallery-card-copy">
                <p>{item.accent}</p>
                <h3>{item.title}</h3>
                <span>{item.location} · {item.room_type}</span>
                <p className="gallery-card-description">{item.description}</p>
              </div>
            </article>
          ))}
        </div>
      </motion.section>

      <motion.section className="estimator-section" id="estimate" variants={sectionReveal} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} data-testid="estimator-section">
        <div className="section-heading-row estimator-heading">
          <div>
            <p className="section-kicker">AI estimator</p>
            <h2 data-testid="estimator-heading">Turn curiosity into a confident budget range.</h2>
          </div>
          <div className="estimate-side-note" data-testid="estimator-side-note">
            Gemini-assisted logic adds local context and prep nuance before your team ever has to call.
          </div>
        </div>
        <div className="estimator-grid">
          <div className="estimator-form-shell" data-testid="estimator-form-shell">
            <div className="estimator-grid-fields">
              <div>
                <label className="quote-label">Service type</label>
                <Select value={estimateForm.service_type} onValueChange={(value) => setEstimateForm((current) => ({ ...current, service_type: value }))}>
                  <SelectTrigger data-testid="estimator-service-trigger"><SelectValue placeholder="Choose a service" /></SelectTrigger>
                  <SelectContent>
                    {["Interior Painting", "Exterior Painting", "Cabinet Painting", "Trim & Doors"].map((service) => (
                      <SelectItem key={service} value={service} data-testid={`estimator-service-option-${service.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{service}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="quote-label">Room / area</label>
                <Input value={estimateForm.room_type} onChange={(event) => setEstimateForm((current) => ({ ...current, room_type: event.target.value }))} data-testid="estimator-room-type-input" />
              </div>
              <div>
                <label className="quote-label">Square feet</label>
                <Input type="number" value={estimateForm.square_feet} onChange={(event) => setEstimateForm((current) => ({ ...current, square_feet: Number(event.target.value) }))} data-testid="estimator-square-feet-input" />
              </div>
              <div>
                <label className="quote-label">Painted surfaces</label>
                <Input type="number" value={estimateForm.surfaces} onChange={(event) => setEstimateForm((current) => ({ ...current, surfaces: Number(event.target.value) }))} data-testid="estimator-surfaces-input" />
              </div>
              <div>
                <label className="quote-label">Finish level</label>
                <Select value={estimateForm.finish_level} onValueChange={(value) => setEstimateForm((current) => ({ ...current, finish_level: value }))}>
                  <SelectTrigger data-testid="estimator-finish-trigger"><SelectValue placeholder="Choose finish" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="standard" data-testid="estimator-finish-standard">Standard</SelectItem>
                    <SelectItem value="premium" data-testid="estimator-finish-premium">Premium</SelectItem>
                    <SelectItem value="designer" data-testid="estimator-finish-designer">Designer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="quote-label">Wall condition</label>
                <Select value={estimateForm.condition} onValueChange={(value) => setEstimateForm((current) => ({ ...current, condition: value }))}>
                  <SelectTrigger data-testid="estimator-condition-trigger"><SelectValue placeholder="Choose condition" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="freshen-up" data-testid="estimator-condition-freshen-up">Freshen up</SelectItem>
                    <SelectItem value="average-walls" data-testid="estimator-condition-average">Average walls</SelectItem>
                    <SelectItem value="patches-and-repairs" data-testid="estimator-condition-repairs">Patches and repairs</SelectItem>
                    <SelectItem value="heavy-prep" data-testid="estimator-condition-heavy-prep">Heavy prep</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="quote-label">Timeline</label>
                <Select value={estimateForm.timeline} onValueChange={(value) => setEstimateForm((current) => ({ ...current, timeline: value }))}>
                  <SelectTrigger data-testid="estimator-timeline-trigger"><SelectValue placeholder="Choose timeline" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="flexible" data-testid="estimator-timeline-flexible">Flexible</SelectItem>
                    <SelectItem value="within-3-weeks" data-testid="estimator-timeline-three-weeks">Within 3 weeks</SelectItem>
                    <SelectItem value="asap" data-testid="estimator-timeline-asap">ASAP</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="quote-label">City</label>
                <Select value={estimateForm.city} onValueChange={(value) => setEstimateForm((current) => ({ ...current, city: value }))}>
                  <SelectTrigger data-testid="estimator-city-trigger"><SelectValue placeholder="Choose city" /></SelectTrigger>
                  <SelectContent>
                    {LOCATION_PAGES.map((location) => (
                      <SelectItem key={location.slug} value={location.city} data-testid={`estimator-city-option-${location.slug}`}>{location.city}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <Textarea placeholder="Optional prep notes, vaulted ceilings, occupancy, feature walls…" value={estimateForm.notes} onChange={(event) => setEstimateForm((current) => ({ ...current, notes: event.target.value }))} className="min-h-[110px]" data-testid="estimator-notes-input" />
            <Button className="cta-primary estimator-button" onClick={submitEstimate} disabled={estimateSubmitting} data-testid="estimator-submit-button">
              {estimateSubmitting ? "Estimating…" : "Show my price range"}
            </Button>
          </div>

          <div className="estimate-result-shell" data-testid="estimate-result-shell">
            {estimateResult?.result ? (
              <>
                <div className="estimate-chip" data-testid="estimate-result-source-chip">
                  <WandSparkles size={15} /> {estimateResult.result.source === "gemini" ? "Gemini-assisted" : "Formula-backed"}
                </div>
                <h3 data-testid="estimate-result-range">
                  ${estimateResult.result.range_low.toLocaleString()}–${estimateResult.result.range_high.toLocaleString()}
                </h3>
                <p data-testid="estimate-result-rationale">{estimateResult.result.rationale}</p>
                <div className="estimate-notes-grid">
                  <div className="estimate-note-card" data-testid="estimate-prep-focus">{estimateResult.result.prep_focus}</div>
                  <div className="estimate-note-card" data-testid="estimate-timeline-note">{estimateResult.result.timeline_note}</div>
                </div>
                <Button className="cta-primary w-full justify-center" onClick={() => setQuoteOpen(true)} data-testid="estimate-accurate-quote-button">
                  Get an accurate quote next
                </Button>
              </>
            ) : (
              <div className="estimate-empty-state" data-testid="estimate-empty-state">
                <Sparkles size={26} />
                <h3>Ready when you are.</h3>
                <p>The estimator outputs a homeowner-friendly range, prep notes, and urgency context in seconds.</p>
              </div>
            )}
          </div>
        </div>
      </motion.section>

      <motion.section className="testimonial-section" variants={sectionReveal} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.18 }} data-testid="testimonials-section">
        <div className="section-heading-row compact">
          <p className="section-kicker">Reviews</p>
          <h2 data-testid="testimonials-heading">Trust signals that look as strong as they convert.</h2>
        </div>
        <div className="testimonial-grid" data-testid="testimonial-grid">
          {(siteData?.testimonials || []).map((item) => (
            <article className="testimonial-card" key={item.id} data-testid={`testimonial-card-${item.id}`}>
              <div className="testimonial-stars" data-testid={`testimonial-stars-${item.id}`}>
                {Array.from({ length: item.rating }).map((_, index) => <Star key={index} size={16} fill="currentColor" />)}
              </div>
              <p className="testimonial-quote">“{item.quote}”</p>
              <div className="testimonial-meta" data-testid={`testimonial-meta-${item.id}`}>
                <strong>{item.name}</strong>
                <span>{item.location} · {item.project_type}</span>
              </div>
            </article>
          ))}
        </div>
      </motion.section>

      <motion.section className="locations-section" variants={sectionReveal} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.18 }} data-testid="locations-section">
        <div className="section-heading-row compact">
          <p className="section-kicker">Local SEO pages</p>
          <h2 data-testid="locations-heading">Neighbourhood-specific paths built for local intent.</h2>
        </div>
        <div className="location-grid" data-testid="locations-grid">
          {LOCATION_PAGES.map((location) => (
            <Link to={`/locations/${location.slug}`} className="location-card" key={location.slug} data-testid={`location-card-${location.slug}`}>
              <span>{location.city}</span>
              <p>{location.summary}</p>
            </Link>
          ))}
        </div>
      </motion.section>

      <motion.section className="blog-section" variants={sectionReveal} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.18 }} data-testid="blog-section">
        <div className="section-heading-row compact">
          <p className="section-kicker">Blog</p>
          <h2 data-testid="blog-heading">SEO-friendly editorial content with clear homeowner value.</h2>
        </div>
        <div className="blog-grid" data-testid="blog-grid">
          {(siteData?.blog_posts || []).map((post) => (
            <Link to={`/blog/${post.slug}`} className="blog-card" key={post.id} data-testid={`blog-card-${post.slug}`}>
              <div className="blog-card-image-shell">
                <img src={post.hero_image} alt={post.title} className="blog-card-image" data-testid={`blog-card-image-${post.slug}`} />
              </div>
              <div className="blog-card-copy">
                <p>{post.category} · {post.read_time}</p>
                <h3>{post.title}</h3>
                <span>{post.excerpt}</span>
              </div>
            </Link>
          ))}
        </div>
      </motion.section>

      <motion.section className="booking-section" id="booking" variants={sectionReveal} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.2 }} data-testid="booking-section">
        <div className="section-heading-row estimator-heading">
          <div>
            <p className="section-kicker">Free on-site estimate</p>
            <h2 data-testid="booking-heading">Book a time without leaving the page.</h2>
          </div>
          <div className="estimate-side-note" data-testid="booking-side-note">
            We keep a rolling {minimumBookingNoticeDays}-day minimum notice so your on-site visit gets realistic planning time without limiting future dates.
          </div>
        </div>
        <div className="booking-grid">
          <div className="booking-calendar-shell" data-testid="booking-calendar-shell">
            <div className="booking-window-note" data-testid="booking-window-note">
              First available date: {dayjs(bookingMinDate).format("MMM D")}. All future dates after that stay open.
            </div>
            <Calendar
              mode="single"
              selected={bookingDate}
              onSelect={(value) => value && setBookingDate(value)}
              disabled={(date) => dayjs(date).isBefore(bookingMinDate, "day")}
              data-testid="booking-calendar"
            />
          </div>
          <div className="booking-form-shell" data-testid="booking-form-shell">
            <div className="time-slot-row" data-testid="booking-time-slot-row">
              {TIME_SLOTS.map((slot) => (
                <button
                  key={slot}
                  type="button"
                  className={`time-slot ${bookingForm.time_label === slot ? "active" : ""}`}
                  onClick={() => setBookingForm((current) => ({ ...current, time_label: slot }))}
                  disabled={bookedSlots.has(slot)}
                  data-testid={`booking-time-slot-${slot.replace(':', '-')}`}
                >
                  {slot}
                </button>
              ))}
            </div>
            <div className="quote-grid quote-grid-wide">
              <Input placeholder="Name" value={bookingForm.name} onChange={(event) => setBookingForm((current) => ({ ...current, name: event.target.value }))} data-testid="booking-name-input" />
              <Input placeholder="Email" type="email" value={bookingForm.email} onChange={(event) => setBookingForm((current) => ({ ...current, email: event.target.value }))} data-testid="booking-email-input" />
              <Input placeholder="Phone" value={bookingForm.phone} onChange={(event) => setBookingForm((current) => ({ ...current, phone: event.target.value }))} data-testid="booking-phone-input" />
              <Select value={bookingForm.city} onValueChange={(value) => setBookingForm((current) => ({ ...current, city: value }))}>
                <SelectTrigger data-testid="booking-city-trigger"><SelectValue placeholder="Choose city" /></SelectTrigger>
                <SelectContent>
                  {LOCATION_PAGES.map((location) => (
                    <SelectItem key={location.slug} value={location.city} data-testid={`booking-city-option-${location.slug}`}>{location.city}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={bookingForm.service_type} onValueChange={(value) => setBookingForm((current) => ({ ...current, service_type: value }))}>
                <SelectTrigger data-testid="booking-service-trigger"><SelectValue placeholder="Choose service" /></SelectTrigger>
                <SelectContent>
                  {["Interior Painting", "Exterior Painting", "Cabinet Painting", "Trim & Doors"].map((service) => (
                    <SelectItem key={service} value={service} data-testid={`booking-service-option-${service.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{service}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Textarea placeholder="Tell us what you're planning." value={bookingForm.notes} onChange={(event) => setBookingForm((current) => ({ ...current, notes: event.target.value }))} className="min-h-[110px]" data-testid="booking-notes-input" />
            <div className="booking-callout" data-testid="booking-selected-slot">
              <CalendarDays size={18} /> {dayjs(bookingDate).format("MMMM D, YYYY")} at {bookingForm.time_label}
            </div>
            <Button className="cta-primary estimator-button" onClick={submitBooking} disabled={bookingSubmitting} data-testid="booking-submit-button">
              {bookingSubmitting ? "Booking…" : "Book free on-site estimate"}
            </Button>
          </div>
        </div>
      </motion.section>

      <motion.section className="faq-section" variants={sectionReveal} initial="hidden" whileInView="visible" viewport={{ once: true, amount: 0.18 }} data-testid="faq-section">
        <div className="section-heading-row compact">
          <p className="section-kicker">Questions</p>
          <h2 data-testid="faq-heading">Everything homeowners ask right before they convert.</h2>
        </div>
        <div className="faq-list" data-testid="faq-list">
          {FAQS.map((item) => (
            <details className="faq-item" key={item.question} data-testid={`faq-item-${item.question.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              <summary data-testid={`faq-summary-${item.question.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>{item.question}</summary>
              <p>{item.answer}</p>
            </details>
          ))}
        </div>
      </motion.section>

      <footer className="site-footer" data-testid="site-footer">
        <div>
          <p className="section-kicker">GTA Modern Painting</p>
          <h2 data-testid="footer-heading">Bold enough to impress. Calm enough to trust.</h2>
        </div>
        <div className="footer-actions" data-testid="footer-actions">
          <a href={BUSINESS.phoneHref} className="footer-link-button" data-testid="footer-phone-button"><PhoneCall size={16} /> Call now</a>
          <a href={`mailto:${BUSINESS.email}`} className="footer-link-button" data-testid="footer-email-button">{BUSINESS.email}</a>
          <a href={BUSINESS.whatsappHref} className="footer-link-button" target="_blank" rel="noreferrer" data-testid="footer-whatsapp-button">WhatsApp</a>
          <button type="button" className="footer-link-button accent" onClick={() => setQuoteOpen(true)} data-testid="footer-quote-button">Get quote</button>
        </div>
      </footer>

      <div className="mobile-sticky-bar" data-testid="mobile-sticky-bar">
        <button type="button" onClick={() => setQuoteOpen(true)} className="mobile-sticky-button accent" data-testid="mobile-quote-button">Free quote</button>
        <a href={BUSINESS.phoneHref} className="mobile-sticky-button" data-testid="mobile-call-button">Call now</a>
        <a href={BUSINESS.whatsappHref} target="_blank" rel="noreferrer" className="mobile-sticky-button" data-testid="mobile-whatsapp-button">WhatsApp</a>
      </div>
    </div>
  );
};
